import React, { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Alert,
  Snackbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Collapse,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Block as BlockIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Home as HomeIcon,
} from '@material-ui/icons'
import {
  GET_AI_COACHING_INTERVENTION,
  UPDATE_AI_COACHING_INTERVENTION,
  SEND_AI_COACHING_EMAIL,
  POST_AI_COACHING_TO_FEED,
  DECLINE_AI_COACHING_INTERVENTION,
  CLAIM_AI_COACHING_INTERVENTION,
} from '../../../../Apollo/Queries/AiCoachingInterventions'
import {
  InterventionHeader,
  MessagePreview,
  FeedbackSection,
  UserInfo,
  PrimaryActionSection,
  SecondaryActionsSection,
  GuidanceSection,
  OrganizationalSection,
  OrganizationTypeLabel,
  OrganizationChipsContainer,
  NoRelationshipsMessage,
  SectionTitle,
  OrganizationLabel,
  NoRelationshipsText,
  OrganizationChip,
} from './CoachingInterventions.styled'

interface RouteParams {
  id: string
}

export const CoachingInterventionDetail: React.FC = () => {
  const { id } = useParams<RouteParams>()
  const history = useHistory()
  const location = useLocation()
  const interventionId = parseInt(id, 10)

  // Get the tab from query params
  const searchParams = new URLSearchParams(location.search)
  const tabFromUrl = searchParams.get('tab')

  const [isEditing, setIsEditing] = useState(false)
  const [editedMessage, setEditedMessage] = useState('')
  const [coachFeedback, setCoachFeedback] = useState('')
  const [coachRating, setCoachRating] = useState<'GOOD' | 'BAD' | null>(null)
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [feedbackExpanded, setFeedbackExpanded] = useState(false)
  const [sendViaEmail, setSendViaEmail] = useState(true)
  const [sendViaFeed, setSendViaFeed] = useState(true)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const { data, loading, error, refetch } = useQuery(
    GET_AI_COACHING_INTERVENTION,
    {
      variables: { id: interventionId },
      skip: !interventionId,
    }
  )

  const [claimIntervention] = useMutation(CLAIM_AI_COACHING_INTERVENTION)
  const [updateIntervention] = useMutation(UPDATE_AI_COACHING_INTERVENTION)
  const [sendEmail] = useMutation(SEND_AI_COACHING_EMAIL)
  const [postToFeed] = useMutation(POST_AI_COACHING_TO_FEED)
  const [declineIntervention] = useMutation(DECLINE_AI_COACHING_INTERVENTION)

  const intervention = data?.aiCoachingIntervention

  useEffect(() => {
    if (intervention) {
      setEditedMessage(intervention.editedMessage || intervention.message || '')
      setCoachFeedback(intervention.coachFeedback || '')
      setCoachRating(intervention.coachRating || null)
      setHasUnsavedChanges(false)
    }
  }, [intervention])

  // Track changes to feedback and rating
  useEffect(() => {
    if (intervention) {
      const feedbackChanged =
        coachFeedback !== (intervention.coachFeedback || '')
      const ratingChanged = coachRating !== (intervention.coachRating || null)
      const messageChanged =
        isEditing &&
        editedMessage !==
          (intervention.editedMessage || intervention.message || '')

      setHasUnsavedChanges(feedbackChanged || ratingChanged || messageChanged)
    }
  }, [coachFeedback, coachRating, editedMessage, isEditing, intervention])

  const handleBack = () => {
    // Navigate back to the tab they came from
    const tabParam = tabFromUrl ? `?tab=${tabFromUrl}` : ''
    history.push(`/internal/self-serve/coaching-interventions${tabParam}`)
  }

  const handleClaim = async () => {
    try {
      const result = await claimIntervention({
        variables: { interventionId },
      })

      if (result.data?.claimAiCoachingIntervention?.success) {
        setSnackbar({
          open: true,
          message: 'Intervention claimed successfully!',
          severity: 'success',
        })
        refetch()
      } else {
        setSnackbar({
          open: true,
          message:
            result.data?.claimAiCoachingIntervention?.message ||
            'Failed to claim intervention',
          severity: 'error',
        })
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error',
      })
    }
  }

  const handleSaveChanges = async () => {
    try {
      const result = await updateIntervention({
        variables: {
          interventionId,
          editedMessage,
          coachFeedback,
          coachRating,
        },
      })

      if (result.data?.updateAiCoachingIntervention?.success) {
        setSnackbar({
          open: true,
          message: 'Changes saved successfully!',
          severity: 'success',
        })
        setIsEditing(false)
        setHasUnsavedChanges(false)
        refetch()
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to save changes',
          severity: 'error',
        })
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error',
      })
    }
  }

  const handleSendToUser = async () => {
    try {
      const promises = []
      const successMessages: string[] = []

      if (sendViaEmail) {
        promises.push(sendEmail({ variables: { interventionId } }))
      }

      if (sendViaFeed) {
        promises.push(postToFeed({ variables: { interventionId } }))
      }

      const results = await Promise.all(promises)

      if (sendViaEmail && results[0]?.data?.sendAiCoachingEmail?.success) {
        successMessages.push('email')
      }

      if (sendViaFeed) {
        const feedIndex = sendViaEmail ? 1 : 0
        if (results[feedIndex]?.data?.postAiCoachingToFeed?.success) {
          successMessages.push('feed')
        }
      }

      if (successMessages.length > 0) {
        setSnackbar({
          open: true,
          message: `Message sent successfully via ${successMessages.join(
            ' and '
          )}!`,
          severity: 'success',
        })
        refetch()
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to send message',
          severity: 'error',
        })
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error',
      })
    }
  }

  const handleDecline = async () => {
    try {
      const result = await declineIntervention({
        variables: { interventionId, reason: declineReason },
      })

      if (result.data?.declineAiCoachingIntervention?.success) {
        setSnackbar({
          open: true,
          message: 'Intervention declined',
          severity: 'success',
        })
        setDeclineDialogOpen(false)
        refetch()
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to decline intervention',
          severity: 'error',
        })
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${
          err instanceof Error ? err.message : 'Unknown error'
        }`,
        severity: 'error',
      })
    }
  }

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' p={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !intervention) {
    return (
      <Box p={4}>
        <Alert severity='error'>
          {error ? error.message : 'Intervention not found'}
        </Alert>
        <Button
          variant='outlined'
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Box>
    )
  }

  const canTakeAction =
    intervention.claimedBy &&
    intervention.status !== 'DECLINED' &&
    intervention.status !== 'POSTED_AND_EMAILED'

  const getStatusColor = (status: string) => {
    if (status === 'NEW') return 'primary'
    if (status === 'DECLINED') return 'error'
    return 'success'
  }

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to Interventions
        </Button>
        <Button
          startIcon={<HomeIcon />}
          onClick={() => history.push('/internal/self-serve')}
          variant='outlined'
        >
          Self-Serve Dashboard
        </Button>
      </Box>

      <InterventionHeader>
        <Box>
          <Typography variant='h4' gutterBottom>
            Intervention #{intervention.id}
          </Typography>
          <Box display='flex' gap={1} alignItems='center'>
            <Chip
              label={intervention.status}
              color={getStatusColor(intervention.status)}
            />
            <Typography variant='body2' color='textSecondary'>
              {intervention.identifier.replace(/([A-Z])/g, ' $1').trim()} •
              Created {new Date(intervention.createdAt).toLocaleDateString()}
            </Typography>
            {intervention.claimedBy && (
              <Typography variant='body2' color='textSecondary'>
                • Claimed by {intervention.claimedBy.firstName}{' '}
                {intervention.claimedBy.lastName}
              </Typography>
            )}
          </Box>
        </Box>
        {intervention.status === 'NEW' && !intervention.claimedBy && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 1,
            }}
          >
            <Button
              variant='contained'
              color='primary'
              onClick={handleClaim}
              size='large'
            >
              Claim This Intervention
            </Button>
            <Typography
              variant='caption'
              color='textSecondary'
              sx={{ textAlign: 'right', maxWidth: 200 }}
            >
              By claiming, you take ownership of this intervention and can send
              it to the user
            </Typography>
          </Box>
        )}
      </InterventionHeader>

      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            User Information
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Basic User Info */}
            <UserInfo>
              <Typography>
                <strong>Name:</strong> {intervention.user?.firstName}{' '}
                {intervention.user?.lastName}
              </Typography>
              <Typography>
                <strong>Email:</strong> {intervention.user?.email}
              </Typography>
            </UserInfo>

            {/* Organizational Information */}
            <OrganizationalSection>
              <SectionTitle variant='subtitle2'>
                Organization & Cohort Information
              </SectionTitle>

              {/* Nonprofit Organizations */}
              {intervention.user?.nonprofitOrgs &&
              intervention.user.nonprofitOrgs.length > 0 ? (
                <OrganizationTypeLabel>
                  <OrganizationLabel variant='body2'>
                    Nonprofit Organization:
                  </OrganizationLabel>
                  <OrganizationChipsContainer>
                    {intervention.user.nonprofitOrgs.map((org: any) => (
                      <OrganizationChip
                        key={org.id}
                        label={org.name}
                        size='small'
                        color={org.disabled ? 'default' : 'primary'}
                        variant={org.disabled ? 'outlined' : 'filled'}
                      />
                    ))}
                  </OrganizationChipsContainer>

                  {/* Cohort Information */}
                  {intervention.user.nonprofitOrgs.some(
                    (org: any) =>
                      org.cohortMemberships && org.cohortMemberships.length > 0
                  ) ? (
                    <OrganizationTypeLabel>
                      <OrganizationLabel variant='body2'>
                        Cohort Memberships:
                      </OrganizationLabel>
                      <OrganizationChipsContainer>
                        {intervention.user.nonprofitOrgs.flatMap((org: any) =>
                          (org.cohortMemberships || []).map(
                            (membership: any) => (
                              <OrganizationChip
                                key={membership.id}
                                label={`${membership.cohort.name} (${new Date(
                                  membership.startDate
                                ).toLocaleDateString()} - ${
                                  membership.endDate
                                    ? new Date(
                                        membership.endDate
                                      ).toLocaleDateString()
                                    : 'Ongoing'
                                })`}
                                size='small'
                                color='secondary'
                                variant='outlined'
                              />
                            )
                          )
                        )}
                      </OrganizationChipsContainer>
                    </OrganizationTypeLabel>
                  ) : null}
                </OrganizationTypeLabel>
              ) : null}

              {/* Funder Organizations */}
              {intervention.user?.funderOrgs &&
              intervention.user.funderOrgs.length > 0 ? (
                <OrganizationTypeLabel>
                  <OrganizationLabel variant='body2'>
                    Funder Organization:
                  </OrganizationLabel>
                  <OrganizationChipsContainer>
                    {intervention.user.funderOrgs.map((org: any) => (
                      <OrganizationChip
                        key={org.id}
                        label={org.name}
                        size='small'
                        color={org.disabled ? 'default' : 'primary'}
                        variant={org.disabled ? 'outlined' : 'filled'}
                      />
                    ))}
                  </OrganizationChipsContainer>
                </OrganizationTypeLabel>
              ) : null}

              {/* No relationships message */}
              {(!intervention.user?.nonprofitOrgs ||
                intervention.user.nonprofitOrgs.length === 0) &&
                (!intervention.user?.funderOrgs ||
                  intervention.user.funderOrgs.length === 0) && (
                  <NoRelationshipsMessage>
                    <NoRelationshipsText variant='body2' color='textSecondary'>
                      No organization or cohort information found
                    </NoRelationshipsText>
                  </NoRelationshipsMessage>
                )}
            </OrganizationalSection>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2}
          >
            <Typography variant='h6'>Message</Typography>
            {canTakeAction && (
              <Button
                startIcon={isEditing ? <CancelIcon /> : <EditIcon />}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Cancel' : 'Edit Message'}
              </Button>
            )}
          </Box>

          {isEditing ? (
            <TextField
              fullWidth
              multiline
              rows={8}
              value={editedMessage}
              onChange={(e) => setEditedMessage(e.target.value)}
              variant='outlined'
            />
          ) : (
            <MessagePreview>
              {intervention.editedMessage || intervention.message}
            </MessagePreview>
          )}
        </CardContent>
      </Card>

      {canTakeAction && (
        <>
          <PrimaryActionSection>
            <GuidanceSection>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <InfoIcon color='primary' />
                <Typography variant='h6' color='primary'>
                  Ready to Send?
                </Typography>
              </Box>
              <Typography variant='body2' color='textSecondary' sx={{ mb: 3 }}>
                Review the message above, make any necessary edits, then choose
                how to deliver it to the user. You can send via email, post to
                their feed, or both.
              </Typography>
            </GuidanceSection>

            <Card
              sx={{
                p: 2,
                backgroundColor: '#f8f9fa',
                border: '1px solid #e3f2fd',
              }}
            >
              <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
                Send Options
              </Typography>

              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendViaEmail}
                      onChange={(e) => setSendViaEmail(e.target.checked)}
                      disabled={!!intervention.emailedAt}
                    />
                  }
                  label='Send via Email'
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendViaFeed}
                      onChange={(e) => setSendViaFeed(e.target.checked)}
                      disabled={!!intervention.postedToFeedAt}
                    />
                  }
                  label="Post to User's Feed"
                />
              </Box>

              <Button
                variant='contained'
                color='primary'
                size='large'
                startIcon={<SendIcon />}
                onClick={handleSendToUser}
                disabled={
                  (!sendViaEmail && !sendViaFeed) ||
                  intervention.status === 'DECLINED' ||
                  (sendViaEmail && !!intervention.emailedAt) ||
                  (sendViaFeed && !!intervention.postedToFeedAt)
                }
                sx={{ width: '100%', py: 1.5 }}
              >
                Send to User
              </Button>
            </Card>
          </PrimaryActionSection>

          <SecondaryActionsSection>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant='h6'>Additional Actions</Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant='outlined'
                startIcon={<ExpandMoreIcon />}
                onClick={() => setFeedbackExpanded(!feedbackExpanded)}
              >
                {feedbackExpanded ? 'Hide' : 'Show'} Feedback Options
              </Button>

              <Button
                variant='outlined'
                color='error'
                startIcon={<BlockIcon />}
                onClick={() => setDeclineDialogOpen(true)}
                disabled={
                  intervention.status === 'DECLINED' ||
                  intervention.status === 'POSTED_AND_EMAILED'
                }
              >
                Decline Intervention
              </Button>
            </Box>

            <Collapse in={feedbackExpanded}>
              <FeedbackSection sx={{ mt: 2 }}>
                <Typography
                  variant='subtitle1'
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Coach Feedback (Optional)
                </Typography>
                <Typography
                  variant='body2'
                  color='textSecondary'
                  sx={{ mb: 2 }}
                >
                  Provide feedback on this AI-generated message to help improve
                  future interventions.
                </Typography>

                <Box mb={2}>
                  <Typography variant='subtitle2' gutterBottom>
                    Rate this message:
                  </Typography>
                  <ToggleButtonGroup
                    value={coachRating}
                    exclusive
                    onChange={(_, value) => setCoachRating(value)}
                  >
                    <ToggleButton value='GOOD'>
                      <ThumbUpIcon style={{ marginRight: 8 }} /> Good
                    </ToggleButton>
                    <ToggleButton value='BAD'>
                      <ThumbDownIcon style={{ marginRight: 8 }} /> Needs
                      Improvement
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label='Feedback Notes (optional)'
                  value={coachFeedback}
                  onChange={(e) => setCoachFeedback(e.target.value)}
                  variant='outlined'
                />

                {hasUnsavedChanges && (
                  <Button
                    variant='contained'
                    startIcon={<SaveIcon />}
                    onClick={handleSaveChanges}
                    sx={{ mt: 2 }}
                  >
                    Save Feedback
                  </Button>
                )}
              </FeedbackSection>
            </Collapse>
          </SecondaryActionsSection>
        </>
      )}

      {intervention.status !== 'NEW' && (
        <Alert severity='info' sx={{ mt: 2 }}>
          This intervention has already been processed.
          {intervention.emailedAt && (
            <Typography variant='body2'>
              Emailed on {new Date(intervention.emailedAt).toLocaleString()}
            </Typography>
          )}
          {intervention.postedToFeedAt && (
            <Typography variant='body2'>
              Posted to feed on{' '}
              {new Date(intervention.postedToFeedAt).toLocaleString()}
            </Typography>
          )}
          {intervention.declinedAt && (
            <Typography variant='body2'>
              Declined on {new Date(intervention.declinedAt).toLocaleString()}
              {intervention.declineReason && `: ${intervention.declineReason}`}
            </Typography>
          )}
        </Alert>
      )}

      <Dialog
        open={declineDialogOpen}
        onClose={() => setDeclineDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Decline Intervention</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Please provide a reason for declining this intervention.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label='Reason for declining'
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            variant='outlined'
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDecline}
            color='error'
            variant='contained'
            disabled={!declineReason.trim()}
          >
            Decline Intervention
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
